import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const slugifyName = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

const getDefaultLogoPath = (name: string): string => `/logos/${slugifyName(name)}.png`;

const ClientLogo: React.FC<{ name: string; logo?: string; decorative?: boolean }> = ({
  name,
  logo,
  decorative = false,
}) => {
  const [isMissing, setIsMissing] = useState(false);
  const [src, setSrc] = useState<string>(logo || getDefaultLogoPath(name));

  useEffect(() => {
    setIsMissing(false);
    setSrc(logo || getDefaultLogoPath(name));
  }, [logo, name]);

  if (isMissing) {
    return <span className="clients-name clients-name-only">{name}</span>;
  }

  return (
    <img
      src={src}
      alt={decorative ? '' : name}
      className="clients-logo"
      width={120}
      height={48}
      onError={() => {
        setIsMissing(true);
      }}
    />
  );
};

const Clients: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [ref, isVisible] = useScrollAnimation(0.15);
  const firstSetRef = useRef<HTMLUListElement>(null);
  const [marqueeWidth, setMarqueeWidth] = useState<number>(0);

  useEffect(() => {
    const updateWidth = () => {
      if (firstSetRef.current) {
        setMarqueeWidth(Math.ceil(firstSetRef.current.scrollWidth));
      }
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    if (firstSetRef.current) {
      observer.observe(firstSetRef.current);
    }
    window.addEventListener('resize', updateWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, [language, t.clients.items]);

  return (
    <section
      id="clients"
      className={`clients-section fade-in ${isVisible ? 'visible' : ''}`}
      ref={ref}
    >
      <div className="container">
        <h2 className="section-title">{t.clients.title}</h2>
        <p className="clients-intro">{t.clients.description}</p>
        <div
          className="clients-marquee"
          role="presentation"
          style={
            marqueeWidth
              ? ({ ['--clients-marquee-width' as string]: `${marqueeWidth}px` } as React.CSSProperties)
              : undefined
          }
        >
          <div className="clients-marquee-track">
            <ul className="clients-marquee-set" ref={firstSetRef}>
              {t.clients.items.map((item, index) => (
                <li key={`a-${item.name}-${index}`} className="clients-item">
                  <div className="clients-brand" title={item.name}>
                    <ClientLogo name={item.name} logo={item.logo} />
                  </div>
                </li>
              ))}
            </ul>
            <ul className="clients-marquee-set clients-marquee-set--duplicate" aria-hidden="true">
              {t.clients.items.map((item, index) => (
                <li key={`b-${item.name}-${index}`} className="clients-item">
                  <div className="clients-brand" title={item.name}>
                    <ClientLogo name={item.name} logo={item.logo} decorative />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Clients;
